import type { TaxEntity } from "../../../shared/domain/entities/TaxEntity";
import { isReverseChargeVendor, lineItemHasTax } from "./reverseCharge";

export type Igst18TaxRef = Pick<
  TaxEntity,
  "tax_id" | "tax_name" | "tax_percentage"
>;

/** Whether system should force IGST 18% (overseas / RCM / non-company currency). */
export function shouldApplyIgst18ForReverseCharge(params: {
  gstTreatment?: string | null;
  gstCategory?: string | null;
  companyCurrencyCode?: string | null;
  documentCurrencyCode?: string | null;
}): boolean {
  if (isReverseChargeVendor(params.gstTreatment ?? undefined, params.gstCategory ?? undefined)) {
    return true;
  }
  const company = String(params.companyCurrencyCode ?? "")
    .trim()
    .toUpperCase();
  const document = String(params.documentCurrencyCode ?? "")
    .trim()
    .toUpperCase();
  return Boolean(company && document && company !== document);
}

export function findIgst18Tax(
  taxes: Array<Igst18TaxRef> | null | undefined,
): Igst18TaxRef | undefined {
  if (!taxes?.length) return undefined;
  return taxes.find((tax) =>
    String(tax.tax_name ?? "")
      .toLowerCase()
      .includes("igst18"),
  );
}

function roundToTwoDecimals(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Force IGST 18% onto taxed line items for overseas / RCM / FX bills.
 * Preserves empty and explicit NO_TAX selections. Returns the same array
 * reference when nothing changes.
 */
export function applyIgst18ToLineItems<T extends Record<string, unknown>>(
  lineItems: T[],
  igst18: Igst18TaxRef,
): T[] {
  if (!lineItems.length) return lineItems;

  const igstPct = parseFloat(String(igst18.tax_percentage));
  let changed = false;

  const updated = lineItems.map((item) => {
    const taxId = String(item.tax_id ?? "").trim();
    if (!lineItemHasTax(taxId)) {
      return item;
    }
    if (taxId === igst18.tax_id) {
      return item;
    }

    changed = true;
    const rate = Number(item.rate ?? 0) || 0;
    const quantity = Number(item.quantity ?? 0) || 0;
    const taxable = rate * quantity;
    const taxAmount =
      !Number.isFinite(igstPct) || igstPct === 0
        ? 0
        : roundToTwoDecimals((taxable * igstPct) / 100);

    return {
      ...item,
      tax_id: igst18.tax_id,
      tax_name: igst18.tax_name,
      tax_percentage: Number.isFinite(igstPct) ? igstPct : 0,
      tax_amount: taxAmount,
      item_total: taxable,
      item_total_inclusive_of_tax: roundToTwoDecimals(taxable + taxAmount),
    };
  });

  return changed ? updated : lineItems;
}
